import { Box, Button, Card, Stack, Typography } from "@mui/material";

import { StyledAnchor } from "@/components/common/styles";
import { newTabProps } from "@/components/marketing/marketingLinks";
import {
  SITE_STEPS,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { CallToAction } from "@/content/types";
import MarketingLottie from "@/components/marketing/MarketingLottie";

/**
 * A copy of the published card's animation, vendored so it is served from this
 * origin; it will not track later edits made in the CMS.
 */
const VISUAL = "/blog/cta-visual.json";
/**
 * The accent lightens on hover here; `marketingPalette.accentHover` is a
 * different step the extractor read off another surface.
 */
const BUTTON_HOVER = "#1EC9A6";

/**
 * The published page carries two copies of this card and shows whichever the
 * width allows. They are not one card at two sizes: the rail's is 400px wide
 * and shrink-to-fit inside, while the one below the article fills the column:
 * a step larger title tracked in at -0.02em, a button that spans the width and
 * a taller, wider illustration. Below the phone step it takes a fourth shape: a
 * deeper top, a narrow gutter and a smaller, heavier title.
 * Measured at 1280, 991, 768, 767, 480 and 479.
 */
const VARIANTS = {
  rail: {
    card: { px: 5, pt: 5, borderRadius: "16px" },
    align: "center",
    bodyGap: 3.5,
    title: { fontSize: 22, lineHeight: "26.4px", letterSpacing: "normal" },
    buttonHeight: 40,
    visual: { width: "76%", pt: "30%" },
    // Hung off the bottom edge, so its height is the artwork's own.
    art: { bottom: "-1%" },
  },
  below: {
    card: { px: "64px", pt: "48px", borderRadius: "16px" },
    align: "stretch",
    bodyGap: 3,
    title: { fontSize: 26, lineHeight: "31.2px", letterSpacing: "-0.02em" },
    buttonHeight: 48,
    visual: { width: "100%", pt: "33%" },
    // Pinned to its box instead, which letterboxes the artwork rather than
    // letting it set its own height.
    art: { top: "-12px", height: "100%" },
  },
} as const;

/** The card below the article takes a third shape under the mobile step, and a fourth under the phone step. */
const BELOW_MOBILE_CARD = { px: "48px", pt: 5, borderRadius: "24px" };
const BELOW_TINY_CARD = { px: "16px", pt: "48px" };
const BELOW_TINY_TITLE = {
  fontSize: 24,
  lineHeight: "28.8px",
  fontWeight: 600,
};

/**
 * The call to action. It has no bottom padding on purpose: the illustration
 * runs past the bottom edge and is cropped by the card's own `overflow`. A
 * field left empty draws nothing, as Webflow hides an empty binding, and the
 * gaps and the frame stay.
 */
export default function PostCtaCard({
  cta,
  variant = "rail",
}: {
  cta: CallToAction;
  variant?: keyof typeof VARIANTS;
}) {
  const shape = VARIANTS[variant];

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: shape.align,
        ...shape.card,
        ...(variant === "below" && {
          [theme.breakpoints.down(SITE_STEPS.card)]: BELOW_MOBILE_CARD,
          [TINY]: BELOW_TINY_CARD,
        }),
        pb: 0,
        backgroundColor: marketingPalette.darkGround,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <Stack
        alignItems={shape.align}
        rowGap={shape.bodyGap}
        sx={{ position: "relative", zIndex: 2 }}
      >
        <Stack alignItems={shape.align} rowGap={2} textAlign="center">
          {cta.title ? (
            <Typography
              component="p"
              sx={{
                ...shape.title,
                fontWeight: 500,
                color: marketingPalette.surface,
                ...(variant === "below" && { [TINY]: BELOW_TINY_TITLE }),
              }}
            >
              {cta.title}
            </Typography>
          ) : null}
          {cta.subtitle ? (
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 300,
                lineHeight: "24px",
                color: marketingPalette.wash,
                opacity: 0.7,
              }}
            >
              {cta.subtitle}
            </Typography>
          ) : null}
        </Stack>
        <Button
          variant="contained"
          disableElevation
          // An empty link field prints "#", as the published template does.
          href={cta.href || "#"}
          {...newTabProps(cta)}
          LinkComponent={StyledAnchor}
          sx={{
            height: shape.buttonHeight,
            // A button with no label is its padding alone, as published.
            minWidth: 0,
            px: 4,
            borderRadius: "8px",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "21px",
            textTransform: "none",
            transition: "0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
            "&:hover": { backgroundColor: BUTTON_HOVER },
          }}
        >
          {cta.buttonLabel}
        </Button>
      </Stack>
      <Box sx={{ position: "relative", ...shape.visual }}>
        <MarketingLottie
          path={VISUAL}
          play={false}
          goTo={0}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            width: "100%",
            transform: "scale(1.2)",
            ...shape.art,
          }}
        />
      </Box>
    </Card>
  );
}
